// Cutscenes on the shot system (gate D1.4; decisions 0043, 0054): a kernel module that plays @rpg/camera timelines when
// story asks (`story:scene`) and says when each has ended (`story:scene-done`).
//
// While a scene plays the world is paused (@rpg/kernel's pause: no physics, no AI, no clock; only this module's system
// runs, `whilePaused`), and the scene runs over it on the sim's fixed step: its time, the cues it fires (lines go out
// as `dialogue:line` through the strings table, poses go to the view, sets and events change state) and the
// actors it moves are plain data in the save. Its camera and actors are sampled by the view at any time between steps
// (src/town/view.ts), so the same seed gives the same frames. Ending, watched or skipped, runs one path: the state cues
// not yet applied (a skip hands them back), each moved actor put on its last mark (an NPC through the townsfolk, the
// hero through a warp), the pause lifted, `story:scene-done`. So the world after a skip is the world after watching.
//
// State: one kernel component (`town/Cutscene`: the scene playing, its queue, the story flags its sets wrote, the poses
// it played and what it has played), saved and hashed.
import type { GameModule, World } from "@rpg/kernel";
import { advanceScene, endMarks, skipScene, startScene, type Cue, type Placed, type SceneResolver, type SceneState } from "@rpg/camera";
import type { GameEvents } from "../core";
import type { SceneDef, TownContent } from "./content";
import { isFresh } from "../save/fresh";

/** The cutscenes' state on a new game: none playing, queued or played. */
const freshCutscenes = (): CutsceneState => ({ playing: null, queue: [], skipAsked: false, flags: {}, poses: [], played: [], steps: 0 });

export const CUTSCENE_C = "town/Cutscene";
/** The kernel pause reason a scene holds. */
export const CUTSCENE_PAUSE = "cutscene";
/** The actions a scene's tools and its skip button dispatch. */
export const CUTSCENE_ACTIONS = { play: "cutscene/play", skip: "cutscene/skip" } as const;

/** The cutscenes' state (a kernel component). */
export interface CutsceneState {
  playing: SceneState | null;
  /** Scenes asked for while another played. */
  queue: string[];
  skipAsked: boolean;
  /** The story flags the scenes' sets wrote (story's state once it merges). */
  flags: Record<string, string | number | boolean>;
  /** The poses the playing scene has started: actor, pose, the scene time it started at. */
  poses: { actor: string; pose: string; at: number }[];
  /** Every scene played: its id, skipped or not, at which sim step. */
  played: { scene: string; skipped: boolean; step: number }[];
  /** Sim steps (all of them, paused or not). */
  steps: number;
}

/** What the cutscenes need from the scene. */
export interface CutsceneHost {
  content: TownContent;
  /** Where an actor stands now (the hero, an NPC, a puppet on its starting mark), or null. */
  actor(id: string): Placed | null;
  mark(id: string): Placed | null;
  /** Put an actor on its last mark when a scene ends (the townsfolk's `place`, the hero's warp). */
  putDown(id: string, at: Placed): void;
  /** A game event a scene names (`warden:awake`). */
  onEvent(name: string): void;
  /** A string's text (lines go out with their text). */
  text(id: string): string;
}

export interface Cutscenes {
  module: GameModule<GameEvents>;
  build(world: World): void;
  hashed: readonly string[];
  state(): CutsceneState;
  /** The scene playing (its definition), or null. */
  scene(): SceneDef | null;
  /** The resolver views sample the camera and actors with. */
  resolver: SceneResolver;
  /** Whether a scene plays now. */
  active(): boolean;
}

export function createCutscenes(host: CutsceneHost): Cutscenes {
  const { content } = host;
  let world: World | null = null;
  let entity = -1;
  const state = (): CutsceneState => {
    if (!world) throw new Error("cutscenes: not built yet");
    return world.getComponent<CutsceneState>(entity, CUTSCENE_C)!;
  };
  const def = (id: string): SceneDef => {
    const d = content.scenes.get(id);
    if (!d) throw new Error(`cutscenes: no scene "${id}"`);
    return d;
  };
  const resolver: SceneResolver = { actor: (id) => host.actor(id), mark: (id) => host.mark(id) };
  let ctxRef: Parameters<GameModule<GameEvents>["setup"]>[0] | null = null;

  const apply = (s: CutsceneState, cues: readonly Cue[]): void => {
    for (const c of cues) {
      if (c.kind === "line") ctxRef!.events.emit("dialogue:line", { speaker: c.speaker, stringId: c.line, text: host.text(c.line), seconds: c.seconds });
      else if (c.kind === "pose") s.poses.push({ actor: c.actor, pose: c.pose, at: c.at });
      else if (c.kind === "set") s.flags[c.key] = c.value;
      else if (c.kind === "event") host.onEvent(c.name);
    }
  };
  const start = (s: CutsceneState, id: string): void => {
    const d = def(id);
    s.playing = startScene(d.timeline, resolver);
    s.poses = [];
    s.skipAsked = false;
    ctxRef!.pause(CUTSCENE_PAUSE, true);
  };
  const finish = (s: CutsceneState, skipped: boolean): void => {
    const p = s.playing!;
    const d = def(p.scene);
    // every moved actor on its last mark (puppets leave with the scene)
    const puppets = new Set(d.puppets.map((x) => x.actor));
    for (const [actor, at] of Object.entries(endMarks(d.timeline, (id) => host.mark(id), p))) if (!puppets.has(actor)) host.putDown(actor, at);
    s.played.push({ scene: p.scene, skipped, step: s.steps });
    s.playing = null;
    s.poses = [];
    s.skipAsked = false;
    ctxRef!.pause(CUTSCENE_PAUSE, false);
    ctxRef!.events.emit("story:scene-done", { scene: p.scene, skipped });
  };

  const step = (dt: number): void => {
    const s = state();
    s.steps++;
    if (!s.playing && s.queue.length) start(s, s.queue.shift()!);
    const p = s.playing;
    if (!p) return;
    const d = def(p.scene);
    if (s.skipAsked) {
      const out = skipScene(d.timeline, p);
      apply(s, out.fired);
      finish(s, true);
      return;
    }
    const out = advanceScene(d.timeline, p, dt);
    apply(s, out.fired);
    if (out.finished) finish(s, false);
  };

  return {
    hashed: [CUTSCENE_C],
    state,
    resolver,
    active: () => !!world && state().playing !== null,
    scene: () => {
      const p = world ? state().playing : null;
      return p ? def(p.scene) : null;
    },
    build(w) {
      world = w;
      entity = w.createEntity();
      w.addComponent<CutsceneState>(entity, CUTSCENE_C, freshCutscenes());
    },
    module: {
      id: "cutscenes",
      version: "0.1.0",
      // checked declarations (decision 0077): what it emits
      provides: { events: ["story:scene-done", "dialogue:line"] },
      setup(ctx) {
        ctxRef = ctx;
        ctx.events.on("story:scene", ({ scene }) => {
          def(scene);
          state().queue.push(scene);
        });
        // first in the step, and the one system that runs while the world is paused
        ctx.registerSystem(({ dt }) => step(dt), { name: "cutscenes:play", order: -300, whilePaused: true });
        ctx.registerAction(CUTSCENE_ACTIONS.play, (p) => {
          const { scene } = p as { scene: string };
          def(scene);
          state().queue.push(scene);
        });
        ctx.registerAction(CUTSCENE_ACTIONS.skip, () => {
          const s = state();
          if (s.playing) s.skipAsked = true;
        });
        ctx.registerSaveSlice({
          serialize: () => structuredClone(state()),
          deserialize: (data) => {
            // a save from before the cutscenes joined the valley (the v6 migration's marker): none played yet
            Object.assign(state(), isFresh(data) ? freshCutscenes() : structuredClone(data as CutsceneState));
            // the pause is the kernel's, not saved: a scene in the save holds it again
            ctx.pause(CUTSCENE_PAUSE, state().playing !== null);
          },
        });
      },
    },
  };
}
